/* sugar:assets/matching-cards.js@2.2.0 */
(function () {
  'use strict';
  if (customElements.get('sugar-matching-cards')) return;
  const fits = new Map();
  const fitSearches = new WeakMap();
  let fitFrame = 0;
  function queueFit(root, reset) {
    fits.set(root, reset || fits.get(root) || false);
    if (fitFrame) return;
    fitFrame = requestAnimationFrame(fitOffers);
  }
  function fitOffers() {
    fitFrame = 0;
    const jobs = Array.from(fits);
    const writes = [];
    fits.clear();
    jobs.forEach(([root, reset]) => {
      if (!root.isConnected) return;
      root._cards.forEach(card => {
        const face = card.querySelector('.sugar-matching-cards__face');
        const text = face.querySelector('.sugar-matching-cards__offer');
        if (!text || !text.textContent) return;
        if (reset) fitSearches.delete(text);
        if (reset && text.style.fontSize) {
          writes.push(() => { text.style.fontSize = ''; queueFit(root, false); });
          return;
        }
        // An offer picture above the text takes its height (and the gap) from the room the text has
        const picture = face.querySelector('.sugar-matching-cards__offer-img');
        const prize = text.parentElement;
        const style = getComputedStyle(face);
        const width = face.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        let height = face.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        if (picture && getComputedStyle(text).position !== 'absolute') {
          height -= picture.offsetHeight + (parseFloat(getComputedStyle(prize).rowGap) || 0);
        }
        if (width <= 0 || height <= 0) return;
        const size = parseFloat(getComputedStyle(text).fontSize);
        // scrollWidth/Height round up while the room keeps its fraction, so a label that fits would
        // otherwise read 1px too wide on every pass and shrink all the way down
        const fitsCard = text.scrollWidth <= Math.ceil(width) && text.scrollHeight <= Math.ceil(height);
        let search = fitSearches.get(text);
        if (!search) {
          if (fitsCard) return;
          search = { low: 1, high: size };
          fitSearches.set(text, search);
        }
        if (fitsCard) search.low = size;
        else search.high = size;
        const finished = search.high - search.low <= 0.15;
        const next = finished ? search.low : Math.floor((search.low + search.high) * 5) / 10;
        if (finished) fitSearches.delete(text);
        if (next === size) return;
        writes.push(() => { text.style.fontSize = next + 'px'; if (!finished) queueFit(root, false); });
      });
    });
    writes.forEach(write => write());
  }

  // [revealed], [required], [remaining], then a [singular|plural] pair chosen by what is left
  function fill(template, counts) {
    return template
      .replace(/\[(revealed|required|remaining)\]/g, (match, key) => counts[key])
      .replace(/\[([^[\]|]*)\|([^[\]]*)\]/g, (match, one, many) => (counts.remaining === 1 ? one : many));
  }
  // The page wraps the count tokens of the blocks below the cards in spans; a block the editor
  // re-renders on its own arrives unwrapped, so wrap any token text left over.
  const TOKEN = /\[(revealed|required|remaining)\]/;
  function wrapTokens(scope) {
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode: node => (TOKEN.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT)
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(node => {
      const parts = node.nodeValue.split(/(\[(?:revealed|required|remaining)\])/);
      const frag = document.createDocumentFragment();
      parts.forEach(part => {
        const match = part.match(/^\[(revealed|required|remaining)\]$/);
        if (!match) {
          if (part) frag.appendChild(document.createTextNode(part));
          return;
        }
        const span = document.createElement('span');
        span.dataset.mcN = match[1];
        frag.appendChild(span);
      });
      node.replaceWith(frag);
    });
  }

  // The shared burst (assets/confetti.js), fetched once, when a game is first played. Every game
  // block loads it through the same tagged script, so a page holding several fetches it once.
  function loadConfetti(src) {
    return new Promise(resolve => {
      if (window.SugarConfetti) return resolve(window.SugarConfetti);
      let script = document.querySelector('script[data-sugar-confetti]');
      if (!script) {
        script = document.createElement('script');
        script.src = src;
        script.setAttribute('data-sugar-confetti', '');
        document.head.appendChild(script);
      }
      script.addEventListener('load', () => resolve(window.SugarConfetti || null));
      script.addEventListener('error', () => resolve(null));
    });
  }
  const RAINBOW = ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff'];

  class MatchingCards extends HTMLElement {
    connectedCallback() {
      if (!this._ready) {
        this._ready = true;
        this._cards = Array.from(this.querySelectorAll('[data-mc-card]'));
        const required = parseInt(this.dataset.mcRequired, 10);
        this._required = Math.min(this._cards.length, Math.max(1, Math.min(5, Number.isFinite(required) ? required : 3)));
        this._status = this.querySelector('[data-mc-status]');
        this._after = this.querySelector('[data-mc-after]');
        this._offers = Array.from(this.querySelectorAll('template[data-mc-offer]'));
        this._picks = [];
        this._won = false;
        this._click = this.onClick.bind(this);
        this._guard = this.onGuard.bind(this);
        this._fit = () => queueFit(this, true);
        this._warm = this.warm.bind(this);
        this._reload = this.onReload.bind(this);
      }
      this.addEventListener('click', this._click);
      this._resize = new ResizeObserver(this._fit);
      this._resize.observe(this.querySelector('.sugar-matching-cards__grid'));
      if (document.fonts) {
        document.fonts.ready.then(() => { if (this.isConnected) this._fit(); });
        document.fonts.addEventListener('loadingdone', this._fit);
      }
      this._fit();
      if (this._after) wrapTokens(this._after);
      if (this.dataset.mcOnReveal === 'unlock') {
        this.addEventListener('click', this._guard, true);
        this.addEventListener('keydown', this._guard, true);
        if (!this._won) this.lock();
      }
      if (window.Shopify && window.Shopify.designMode) document.addEventListener('shopify:block:load', this._reload);
      this.paintCounts();
      // Prize pictures and the confetti are fetched once the page has loaded, so the first pick
      // never waits on them and they never compete with the page's first paint.
      this.grid().addEventListener('pointerover', this._warm, { once: true });
      if (document.readyState === 'complete') this.idle(this._warm);
      else window.addEventListener('load', () => this.idle(this._warm), { once: true });
      this._popup = this.closest('.sugar-email-popup');
      if (this._popup && this.dataset.mcOnReveal === 'continue') {
        this._observer = new MutationObserver(() => {
          if (this._popup.classList.contains('is-open') && this._won) this.queueContinue();
          else if (!this._popup.classList.contains('is-open')) clearTimeout(this._timer);
        });
        this._observer.observe(this._popup, { attributes: true, attributeFilter: ['class'] });
        if (this._won) this.queueContinue();
      }
    }
    disconnectedCallback() {
      clearTimeout(this._timer);
      if (this._observer) this._observer.disconnect();
      if (this._resize) this._resize.disconnect();
      if (document.fonts) document.fonts.removeEventListener('loadingdone', this._fit);
      document.removeEventListener('shopify:block:load', this._reload);
      fits.delete(this);
      this.removeEventListener('click', this._click);
      this.removeEventListener('click', this._guard, true);
      this.removeEventListener('keydown', this._guard, true);
    }
    // How many cards have been revealed (Gift Unlocks set to Matching Cards picks reads it)
    get revealed() {
      return this._picks ? this._picks.length : 0;
    }
    grid() {
      return this.querySelector('.sugar-matching-cards__grid');
    }
    idle(fn) {
      if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 200);
    }
    warm() {
      if (this._warmed) return;
      this._warmed = true;
      this._held = [];
      this._offers.forEach(template => {
        template.content.querySelectorAll('img').forEach(img => {
          const copy = new Image();
          if (img.getAttribute('sizes')) copy.sizes = img.getAttribute('sizes');
          if (img.getAttribute('srcset')) copy.srcset = img.getAttribute('srcset');
          copy.src = img.getAttribute('src');
          this._held.push(copy);
        });
      });
      if (this.dataset.mcConfetti) loadConfetti(this.dataset.mcConfetti);
    }
    counts() {
      const revealed = this._picks.length;
      return { revealed, required: this._required, remaining: Math.max(0, this._required - revealed) };
    }
    paintCounts() {
      const counts = this.counts();
      this.querySelectorAll('[data-mc-n]').forEach(el => {
        const value = String(counts[el.dataset.mcN]);
        if (el.textContent !== value) el.textContent = value;
      });
      if (this._label) {
        const label = fill(this.dataset.mcLockedText || '', counts);
        if (this._label.textContent !== label) this._label.textContent = label;
      }
    }
    // Unlock blocks below cards: every action below the cards stays out of reach until the game
    // is won, and the first button reads the locked label.
    lock() {
      if (!this._after) return;
      this._after.querySelectorAll('a[href], button, input, select, textarea').forEach(el => {
        if (el.hasAttribute('data-mc-held')) return;
        el.setAttribute('data-mc-held', el.getAttribute('tabindex') || '');
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-disabled', 'true');
      });
      this._label = this._after.querySelector('[data-mc-locked-label]');
      const first = this._after.querySelector('.sugar-link-button__btn, [data-popup-button]');
      if (!this._label && first && this.dataset.mcLockedText) {
        this._label = document.createElement('span');
        this._label.className = 'sugar-matching-cards__locked-label';
        this._label.setAttribute('data-mc-locked-label', '');
        const own = first.querySelector('.sugar-link-button__text');
        if (own) own.before(this._label);
        else {
          const wrap = document.createElement('span');
          while (first.firstChild) wrap.appendChild(first.firstChild);
          first.append(this._label, wrap);
        }
      }
    }
    unlock() {
      this.removeAttribute('data-mc-locked');
      this._after.querySelectorAll('[data-mc-held]').forEach(el => {
        const tabindex = el.getAttribute('data-mc-held');
        if (tabindex) el.setAttribute('tabindex', tabindex);
        else el.removeAttribute('tabindex');
        el.removeAttribute('aria-disabled');
        el.removeAttribute('data-mc-held');
      });
    }
    onGuard(event) {
      if (!this.hasAttribute('data-mc-locked') || !this._after || !this._after.contains(event.target)) return;
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
    }
    // The editor re-renders a block below the cards on its own: count it (and lock it) again
    onReload(event) {
      if (!this._after || !this._after.contains(event.target)) return;
      wrapTokens(this._after);
      if (this.dataset.mcOnReveal === 'unlock' && !this._won) this.lock();
      this.paintCounts();
    }
    queueContinue() {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        if (this.isConnected && this._popup.classList.contains('is-open') && typeof this._popup.emailPopupShowOptIn === 'function') {
          this._popup.emailPopupShowOptIn();
        }
      }, 1000);
    }
    // Status and card labels come translated from the block (locales: general.matching_cards).
    text(key, fallback, offer) {
      return (this.dataset[key] || fallback).replace('[revealed]', this._picks.length).replace('[offer]', offer);
    }
    // The n-th pick shows the n-th offer, cycling; with one offer every face already holds it
    dealOffer(card) {
      const prize = card.querySelector('[data-mc-prize]');
      if (this._offers.length > 1 && prize) {
        const template = this._offers[this._picks.length % this._offers.length];
        prize.replaceChildren(template.content.cloneNode(true));
        queueFit(this, false);
      }
      const text = prize && prize.querySelector('.sugar-matching-cards__offer');
      const picture = prize && prize.querySelector('.sugar-matching-cards__offer-img');
      return ((text && text.textContent.trim()) || (picture && picture.alt) || '').trim();
    }
    onClick(event) {
      const card = event.target.closest('[data-mc-card]');
      if (!card || !this._cards.includes(card) || this._won || this._picks.includes(card)) return;
      this.warm();
      const offer = this.dealOffer(card);
      card.setAttribute('aria-pressed', 'true');
      card.setAttribute('aria-label', this.text('mcRevealedLabel', 'Revealed offer: [offer]', offer));
      card.disabled = true;
      this._picks.push(card);
      this._status.textContent = this.text('mcProgress', '[revealed] of ' + this._required + ' cards revealed: [offer].', offer);
      this.paintCounts();
      this.dispatchEvent(new CustomEvent('matching-cards:progress', { bubbles: true, detail: Object.assign({ offer }, this.counts()) }));
      if (this._picks.length < this._required) return;
      this._won = true;
      this.setAttribute('data-mc-revealed', '');
      this._cards.forEach(pick => { pick.disabled = true; });
      this._status.textContent = this.text('mcComplete', 'Offer revealed! Your offer is [offer].', offer);
      this.dispatchEvent(new CustomEvent('matching-cards:reveal', { bubbles: true }));
      if (this.dataset.mcConfetti) {
        // The burst lands as the last card finishes turning
        loadConfetti(this.dataset.mcConfetti).then(burst => {
          setTimeout(() => {
            if (burst) burst.burst({ look: 'cannon', origin: { x: 0.5, y: 0.6 }, colors: RAINBOW, count: 100, spread: 70 });
          }, 250);
        });
      }
      if (this.dataset.mcOnReveal === 'continue' && this._popup) {
        this.queueContinue();
        return;
      }
      if (this.dataset.mcOnReveal === 'unlock') {
        this.unlock();
        return;
      }
      this._after.hidden = false;
      const next = this._after.querySelector('button, a[href], input');
      if (next) next.focus({ preventScroll: true });
    }
  }
  customElements.define('sugar-matching-cards', MatchingCards);
})();
